import { NavLink } from "react-router-dom";
import { useRegistrationStatus } from "../hooks/useRegistrationStatus";
import { motion } from "framer-motion";
import { BrainCog, CheckCheck, FileText, Globe, Users2 } from "lucide-react";
import SiteNavbar from "../components/SiteNavbar";
// Light-mode hero is bundled locally so it benefits from optimisation.
import heroImgLight from "../assets/images/hero-landing-pexels.jpeg";

// Dark-mode hero also comes from Pexels but is now bundled locally so the app
// doesn’t rely on external asset hosts at runtime.
import heroImgDark from "../assets/images/hero-landing-dark-pexels.jpeg";
import dashboardImg from "../assets/images/hero-dashboard.jpeg";
import teacherImg from "../assets/images/teacher-classes.jpeg";


export default function LandingPage() {
  const { waitlist } = useRegistrationStatus();
  // DEBUG: log status during development
  if (import.meta.env.DEV) console.log('LandingPage waitlist:', waitlist);

  // Determine CTA copy based on registration capacity. While the status
  // loads we fall back to the non-waitlist wording so the layout remains
  // stable; the button updates once the async hook resolves.
  const heroCtaLabel = waitlist ? "Join Waitlist" : "Create free account";
  const footerCtaLabel = heroCtaLabel;

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <SiteNavbar />

      {/* ---------------------------------- */}
      {/* Hero                             */}
      {/* ---------------------------------- */}

      <section className="relative flex-1">
        {/* Background images – swap based on colour scheme */}
        <img
          src={heroImgLight}
          alt="Teacher collaborating with students in bright classroom"
          className="theme-light-only absolute inset-0 h-full w-full object-cover"
        />
        <img
          src={heroImgDark}
          alt="A dramatic, low-light image of a slightly open laptop with glowing keys on a dark desk"
          className="theme-dark-only absolute inset-0 h-full w-full object-cover"
        />

        {/* Gradient overlay – darker for light mode; keep subtle tint in dark */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/40 to-white dark:from-gray-900/70 dark:via-gray-900/40 dark:to-gray-900" />

        <div className="relative z-10 mx-auto flex max-w-7xl flex-col items-center justify-center gap-10 px-6 py-40 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl text-4xl font-extrabold tracking-tight text-gray-900 dark:text-gray-50 md:text-6xl"
          >
            Spend your time mentoring, not measuring
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="max-w-2xl text-lg text-gray-700 dark:text-gray-300 md:text-2xl"
          >
            ItsAcademic quietly checks references, style-guide compliance and rubric alignment so you can pour your energy into the part no algorithm can replace — genuine, one-to-one feedback.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="flex flex-col items-center gap-4 sm:flex-row"
          >
            <NavLink
              to="/signup"
              className="rounded-md bg-primary px-8 py-4 text-base font-semibold text-white shadow hover:bg-primary-dark focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900"
            >
              {heroCtaLabel}
            </NavLink>
            <a
              href="#how-it-works"
              className="rounded-md border border-gray-300 px-8 py-4 text-base font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 outline-hidden"
            >
              See how it works
            </a>
          </motion.div>

          {/* Photo credits (light) */}
          <p className="mt-8 text-xs text-gray-500 dark:hidden">
            Photo by{' '}
            <a
              href="https://www.pexels.com/@thisisengineering"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              ThisIsEngineering
            </a>{' '}
            on{' '}
            <a href="https://www.pexels.com" target="_blank" rel="noreferrer" className="underline">
              Pexels
            </a>
          </p>

          {/* Photo credits (dark) */}
          <p className="mt-8 hidden text-xs text-gray-400 dark:block">
            Photo by{' '}
            <a
              href="https://www.pexels.com/@danbuilds"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Daniel Putzer
            </a>{' '}
            on{' '}
            <a href="https://www.pexels.com" target="_blank" rel="noreferrer" className="underline">
              Pexels
            </a>
          </p>
        </div>
      </section>

      {/* ---------------------------------- */}
      {/* Credibility banner (no vanity #s)  */}
      {/* ---------------------------------- */}
      <section className="bg-gray-50 py-12 dark:bg-gray-800">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <p className="text-base text-gray-700 dark:text-gray-300">
            Built by academics, for academics. Currently in private pilot with a small group of universities and
            colleges.
          </p>
        </div>
      </section>

      {/* ---------------------------------- */}
      {/* Feature Highlights                */}
      {/* ---------------------------------- */}
      <section className="mx-auto w-full max-w-7xl px-6 py-24 dark:text-gray-50">
        <h2 id="how-it-works" className="mb-16 text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50 md:text-4xl">
          Offload the admin. Keep the mentoring.
        </h2>

        <div className="grid gap-16 md:grid-cols-2 md:gap-24">
          {/* Feature 1 */}
          <div className="flex flex-col justify-center gap-6">
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-50">Instant reference & style checks</h3>
            <p className="text-gray-700 dark:text-gray-300">
              Upload an assessment and our agent flags broken URLs, missing citations and deviation from your chosen
              style guide (APA, MLA, Chicago & more) before you start reading.
            </p>
            <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <BrainCog className="h-5 w-5 text-primary" /> You approve — we only surface issues.
            </p>
          </div>
          <img
            src={dashboardImg}
            alt="Screenshot of ItsAcademic grading dashboard"
            className="w-full rounded-lg shadow-lg"
          />

          {/* Feature 2 */}
          <img
            src={teacherImg}
            alt="Teacher engaging with students"
            className="order-last w-full rounded-lg shadow-lg md:order-none"
          />
          <div className="flex flex-col justify-center gap-6">
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-50">Auto-mapped to your rubric</h3>
            <p className="text-gray-700 dark:text-gray-300">
              See at a glance where the submission meets or misses each criterion. No more cross-referencing columns in
              a spreadsheet.
            </p>
            <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Users2 className="h-5 w-5 text-primary" /> Fully editable before any score is released
            </p>
          </div>
        </div>
      </section>

      {/* ---------------------------------- */}
      {/* Feature grid                      */}
      {/* ---------------------------------- */}
      <section className="mx-auto w-full max-w-7xl px-6 py-24 dark:text-gray-50">
        <h2 className="mb-12 text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50 md:text-4xl">
          Why educators choose ItsAcademic
        </h2>

        <div className="grid gap-8 md:grid-cols-3">
          {[
            { title: '1-click LMS import', copy: 'Connect Canvas, Blackboard, Moodle or Google Classroom in seconds.', icon: Globe },
            { title: 'Rich rubric templates', copy: 'Build once, reuse forever — or pick from discipline-specific presets.', icon: FileText },
            { title: 'Autonomous but editable', copy: 'AI drafts, you approve. Stay compliant with institutional policy.', icon: CheckCheck },
          ].map((f) => (
              <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5 }}
              className="rounded-lg bg-gray-50 dark:bg-gray-800 p-8 shadow-sm"
            >
              <f.icon className="mb-4 h-8 w-8 text-primary" />
              <h3 className="mb-2 text-xl font-semibold text-gray-900 dark:text-gray-50">{f.title}</h3>
              <p className="text-gray-600 dark:text-gray-400">{f.copy}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials removed until we have real pilot feedback */}

      {/* ---------------------------------- */}
      {/* CTA                               */}
      {/* ---------------------------------- */}
      <section className="bg-primary py-24 text-center text-white">
        <h2 className="mb-8 text-3xl font-bold md:text-4xl">Curious to see it in action?</h2>
        <NavLink
          to="/signup"
          className="inline-block rounded-md bg-white px-10 py-4 text-base font-semibold text-primary shadow hover:bg-gray-100"
        >
          {footerCtaLabel}
        </NavLink>
      </section>

      {/* Footer */}
      <footer className="border-t bg-gray-50 dark:bg-gray-900 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        © {new Date().getFullYear()} ItsAcademic.ai — created on Gadigal and Wangal Country by the team at Aperim Pty Ltd.
      </footer>
    </div>
  );
}
